import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscribeTenants } from '../../data/listeners/tenants';
import { adminCreateTenant } from '../../data/commands/tenantCommands';
import Icon from '../../components/Icon';

const slugify = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
const fmt = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

// Add a tenant + its first Owner (one gated server call). The owner can then log
// in (with the temp password shown on success) or HQ can switch in to continue.
function AddTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', tenantId: '', ownerName: '', ownerEmail: '', ownerPassword: 'Presto-2026!',
  });
  const [idEdited, setIdEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Auto-derive the id from the name until HQ types their own.
  const setName = (e) => setForm((f) => ({ ...f, name: e.target.value, tenantId: idEdited ? f.tenantId : slugify(e.target.value) }));
  const setId = (e) => { setIdEdited(true); setForm((f) => ({ ...f, tenantId: slugify(e.target.value) })); };

  async function save() {
    if (!form.name.trim()) { setError('Business / tenant name is required.'); return; }
    if (!form.tenantId) { setError('Tenant id could not be derived — enter one.'); return; }
    if (!form.ownerName.trim() || !form.ownerEmail.trim()) { setError('Owner name and email are required.'); return; }
    if (!form.ownerPassword) { setError('A temporary password is required.'); return; }
    setBusy(true); setError('');
    try {
      const res = await adminCreateTenant({
        name: form.name.trim(),
        tenantId: form.tenantId,
        ownerName: form.ownerName.trim(),
        ownerEmail: form.ownerEmail.trim(),
        ownerPassword: form.ownerPassword,
      });
      onCreated({ ...res, name: form.name.trim(), ownerEmail: form.ownerEmail.trim(), ownerPassword: form.ownerPassword });
    } catch (e) {
      setError(e.message || 'Could not create the tenant.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header><Icon name="plus" size={18} /> Add tenant</header>
        <div className="body">
          <div className="field"><label>Business / tenant name</label><input className="input" value={form.name} onChange={setName} placeholder="e.g. Sparkle Laundry Cebu" /></div>
          <div className="field"><label>Tenant ID</label><input className="input" value={form.tenantId} onChange={setId} placeholder="sparkle-laundry-cebu" /><span className="muted" style={{ fontSize: 'var(--text-caption)' }}>Lowercase, used internally; auto-filled from the name.</span></div>
          <div className="lead-section"><Icon name="users" size={16} /> First owner (continues the setup)</div>
          <div className="field"><label>Owner full name</label><input className="input" value={form.ownerName} onChange={set('ownerName')} placeholder="Juan dela Cruz" /></div>
          <div className="field"><label>Owner email</label><input className="input" type="email" value={form.ownerEmail} onChange={set('ownerEmail')} placeholder="owner@sparkle.ph" /></div>
          {/* BUG-023: admin sets a temp password for the new owner — keep password
              managers out of this React-managed field (see UserManagement). */}
          <div className="field"><label>Temporary password</label><input className="input" type="text" autoComplete="off" value={form.ownerPassword} onChange={set('ownerPassword')} /></div>
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Creating…' : 'Create tenant + owner'}</button>
        </footer>
      </div>
    </div>
  );
}

export default function Tenants() {
  const { role, activeTenantId, setActiveTenant } = useAuth();
  const isHq = role === 'hqadmin';
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [created, setCreated] = useState(null); // { tenantId, ownerEmail, ownerPassword, name }

  useEffect(() => {
    if (!isHq) return undefined;
    return subscribeTenants(setTenants, (e) => setError(e.message || 'Could not load tenants.'));
  }, [isHq]);

  const rows = useMemo(() => [...tenants].sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id)), [tenants]);

  if (!isHq) {
    return (
      <div className="card card-pad" style={{ maxWidth: 560 }}>
        <b>HQ Admin only.</b>
        <p className="muted" style={{ fontSize: 'var(--text-small)', marginTop: 8 }}>Creating and switching between tenants is restricted to HQ Admin.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <div className="toolbar">
        <span style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => { setCreated(null); setShowAdd(true); }}><Icon name="plus" size={15} /> Add tenant</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      {created && (
        <div className="card card-pad" style={{ background: 'var(--presto-success-bg)', borderColor: '#bfe3cf' }}>
          <b>Tenant “{created.name}” created.</b>
          <p style={{ fontSize: 'var(--text-small)', margin: '8px 0' }}>
            Owner login — <b>{created.ownerEmail}</b> · temporary password <b>{created.ownerPassword}</b> (share securely; they can change it after first login).
          </p>
          <button className="btn btn-primary btn-sm" onClick={() => { setActiveTenant(created.tenantId); setCreated(null); }}>
            Switch into “{created.name}” to continue setup
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Tenant</th><th>ID</th><th>Status</th><th>Created</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id}>
                <td style={{ fontWeight: 'var(--font-semibold)' }}>{t.name ?? t.id}{t.id === activeTenantId && <span className="badge tier-Anchor" style={{ marginLeft: 8 }}>Active</span>}</td>
                <td className="muted">{t.id}</td>
                <td><span className={`badge tier-${t.status === 'active' ? 'Anchor' : 'Small'}`}>{t.status ?? 'active'}</span></td>
                <td className="muted">{fmt(t.createdAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-ghost btn-sm" disabled={t.id === activeTenantId} onClick={() => setActiveTenant(t.id)}>
                    {t.id === activeTenantId ? 'Current' : 'Switch'}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No tenants yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="muted" style={{ fontSize: 'var(--text-caption)' }}>
        After creating a tenant, switch into it (or have the owner log in) and use User Management, Stores / Hubs and Territory Settings to finish the setup.
      </p>

      {showAdd && (
        <AddTenantModal
          onClose={() => setShowAdd(false)}
          onCreated={(res) => { setShowAdd(false); setCreated(res); }}
        />
      )}
    </div>
  );
}
